import { cn } from "@/lib/utils";

/**
 * Brand logo. Two layouts share the same source files:
 *   - `icon` (compact) renders the standalone orange "7" mark — used
 *     for tight slots like favicons and collapsed sidebars.
 *   - `wordmark` (full) renders the brand wordmark — used in headers
 *     and login hero. We dark-invert in dark mode so the dark text
 *     remains legible without shipping a second asset.
 *
 * `cn()` is used so a caller-supplied `className` (e.g. `h-11`) wins
 * over the variant default size via twMerge.
 */
interface LogoProps {
  variant?: "icon" | "wordmark" | "stack";
  className?: string;
}

export function Logo({ variant = "stack", className }: LogoProps) {
  if (variant === "icon") {
    return (
      <img
        src="/logos/afya24-icon.png"
        alt="Afya24"
        className={cn("h-9 w-auto", className)}
      />
    );
  }

  if (variant === "wordmark") {
    return (
      <img
        src="/logos/afya24-wordmark.png"
        alt="Afya24"
        // dark:invert flips the dark wordmark glyphs to light in dark
        // mode. The orange "7" picks up a slight hue shift but stays
        // brand-recognisable and saves shipping a second PNG.
        className={cn("h-10 w-auto", className)}
      />
    );
  }

  // Default stack: icon + wordmark + "Admin" caption — used when a
  // caller explicitly opts in. The sidebar uses `wordmark` directly.
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <img src="/logos/afya24-icon.png" alt="Afya24" className="h-9 w-auto" />
      <div className="flex flex-col leading-tight">
        <img
          src="/logos/afya24-wordmark.png"
          alt="Afya24"
          className="h-4 w-auto"
        />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          Admin
        </span>
      </div>
    </div>
  );
}
